import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-context";
import { Dialog, DialogContent } from "@mui/material";
import LoadingPlaceHolder from "src/components/molecules/loading/loading-placeholder";
import BusinessNumbersList from "../business-numbers-list/business-numbers-list";
import {
	IApplicationModel,
	IBusinessModel,
	IBusinessNumberModel,
	TOutboundCallFallBackEvent,
	TCallRoutingOption,
	IPhoneNumber,
	IFileInfo,
} from "src/types/models/apiModels";
import ApplicationCredentials from "../application-credentials/application-credentials";
import { EditApplicationViewModel } from "./application-edit.view-model";
import Card from "src/components/molecules/card/card";
import ErrorPopup from "../error-popup/error-popup";
import BrandingConfiguration from "../branding-configuration/branding-configuration";
import ButtonAddNew from "src/components/atoms/button-add-new/button-add-new";
import ActionPanel from "../action-panel/action-panel";
import { BusinessNumbersConfigureViewModel } from "../business-numbers-configure/business-numbers-configure.view-model";
import StatusBadge from "src/components/molecules/status-badge/status-badge";
import ApplicationDownloads from "../application-downloads/application-downloads";
import Banner from "src/components/molecules/banner/Banner";
import { ApplicationListViewModel } from "../application-list/application-list.view-model";
import PhoneNumberDisplay from "src/components/atoms/phone-number-display/phone-number-display";
import Alert from "src/components/atoms/alert/alert";
import RadioGroup from "src/components/molecules/radio-button-group/radio-button-groups";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import CheckBox from "src/components/molecules/check-box/check-box";
import mobileApp from "../mobile-app/mobile-app";
import { ReactComponent as IncomingCallIcon } from "../../../assets/svg/incomingCallIcon.svg";
import { ReactComponent as OutgoingCallIcon } from "../../../assets/svg/outgoingCallIcon.svg";
import { ReactComponent as HistoryIcon } from "../../../assets/svg/historyIcon.svg";
import ApplicationSubmitProcessor from "./application-submit-processor";
import { defaultScreenSetting } from "src/models/default-screen-settings";
import { CopyIcon } from "lucide-react";
import { ClipboardService } from "src/helpers/clipboard-service";
import RadioGroupSquare from "src/components/molecules/radio-button-group/radio-button-groups-square";
import PhoneNumberField from "src/components/molecules/phoneNumberField/PhoneNumberField";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import UploadButton from "src/components/molecules/upload-button/upload-button";

interface ApplicationEditProps {
	onSubmitClick?: (application: IApplicationModel) => void;
	onCancelClick?: () => void;
	applicationId: string;
}

const ApplicationEdit: React.FC<ApplicationEditProps> = ({
	onSubmitClick,
	onCancelClick,
	applicationId,
}) => {
	const DELETEBRANDINGAPPLICATION = "DELETE BRANDING APPLICATION";
	const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
	const [applicationDeleteError, setApplicationDeleteError] = useState("");
	const applicationListViewModel = useRef<ApplicationListViewModel | null>(
		null
	);
	const [applicationFriendlyName, setApplicationFriendlyName] = useState("");
	const [description, setDescription] = useState("");
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const { user } = useAuth(); // Extract user from useAuth hook
	const [loading, setLoading] = useState(false);
	const [applicationSubmitDialogOpen, setApplicationSubmitDialogOpen] =
		useState(false);
	const [businessNumberFilteredValue, setBusinessNumberFilteredValue] =
		useState<string>("");
	const [applicationNumbers, setApplicationNumbers] = useState<
		IBusinessNumberModel[]
	>([]);
	const [unLinkedBusinessNumbers, setUnLinkedBusinessNumbers] = useState<
		IBusinessNumberModel[]
	>([]);
	const [application, setApplication] = useState<IApplicationModel>();
	const [hasChanges, setHasChanges] = useState(false);
	const [inboundSipDomain, setInboundSipDomain] = useState("");
	const [numberDialogOpen, setNumberDialogOpen] = useState<boolean>(false);
	const [showDownloads, setShowDownloads] = useState<boolean>(false);
	const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
	const [showApplicationCredentials, setShowApplicationCredentials] =
		useState(false);
	const [showHelp, setShowHelp] = useState(false);
	const [error, setError] = useState<string>("");
	const [inboundCallAnnouncementText, setInboundCallAnnouncementText] =
		useState("");
	const [outboundCallAnnouncementText, setOutboundCallAnnouncementText] =
		useState("");
	const [enableInboundCalling, setEnableInboundCalling] = useState(false);
	const [routeIncomingCallsTo, setRouteIncomingCallsTo] =
		useState<TCallRoutingOption>("SIP");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [inboundPhoneNumber, setInboundPhoneNumber] = useState<IPhoneNumber>({
		countryCode: "",
		number: "",
	});
	const [requestUserLocation, setRequestUserLocation] = useState(false);
	const [useLocationBasedCallRouting, setLocationBasedCallRouting] =
		useState(false);
	const [routeOutgoingCallsTo, setRouteOutgoingCallsTo] =
		useState<TCallRoutingOption>();
	const [fallbackEvents, setFallbackEvents] = useState<string[] | undefined>(
		undefined
	);
	const viewModel = useRef<EditApplicationViewModel | null>(null);
	const businessNumbersConfigureViewModel =
		useRef<BusinessNumbersConfigureViewModel | null>(null);
	const [business, setBusiness] = useState<IBusinessModel | undefined>(
		undefined
	);
	const [approvedBusinessNumbers, setApprovedBusinessNumbers] = useState<
		IBusinessNumberModel[]
	>([]);
	const [hasFailedEventSubscribed, setHasFailedEventSubscribed] =
		useState(false);
	const [hasUnAnsweredEventSubscribed, setHasUnAnsweredEventSubscribed] =
		useState(false);
	const [
		showApplicationDeleteConfirmation,
		setShowApplicationDeleteConfirmation,
	] = useState(false);
	const [ringerTimeout, setRingerTimeout] = useState<number>();

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				if (user && user.accessToken) {
					viewModel.current = new EditApplicationViewModel(
						user.accessToken
					);

					const app =
						await viewModel.current?.fetchApplication(
							applicationId
						);

					if (app) {
						setApplication(app);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	useEffect(() => {
		if (application) {
			setBusiness(application.business);
			setApplicationFriendlyName(application.name);
			setDescription(application.description);
			setApplicationNumbers(application.numbers);
			setRouteOutgoingCallsTo(
				application.managedApplicationSettings?.outBoundCall
					.routingOption
			);

			if (
				application &&
				application.managedApplicationSettings &&
				application.managedApplicationSettings.inboundCall &&
				application.managedApplicationSettings.inboundCall.routingOption
			) {
				setRouteIncomingCallsTo(
					application.managedApplicationSettings?.inboundCall
						?.routingOption
				);
			} else {
				setRouteIncomingCallsTo("SIP");
			}

			setRingerTimeout(
				application.managedApplicationSettings?.outBoundCall.timeout ||
					10
			);

			setOutboundCallAnnouncementText(
				application.managedApplicationSettings?.outBoundCall
					.announcementText ?? ""
			);
			setEnableInboundCalling(
				application.managedApplicationSettings?.inboundCall
					? true
					: false
			);
			setInboundCallAnnouncementText(
				application.managedApplicationSettings?.inboundCall
					?.announcementText ?? ""
			);
			setInboundSipDomain(
				application.managedApplicationSettings?.inboundCall
					?.sipDomain ?? ""
			);
			setPhoneNumber(
				application.managedApplicationSettings?.inboundCall
					?.phoneNumber ?? ""
			);
			setHasFailedEventSubscribed(
				application.managedApplicationSettings?.outBoundCall.fallBackEvents.some(
					(event) => event === "failed"
				) ?? false
			);

			setHasUnAnsweredEventSubscribed(
				application.managedApplicationSettings?.outBoundCall.fallBackEvents.some(
					(event) => event === "timeout"
				) ?? false
			);

			if (application.numbers.length > 0) {
				toggleAccordion(0);
			}
		}
	}, [application]);

	useEffect(() => {
		const fetchBusinessNumbers = async () => {
			if (business) {
				try {
					if (user && user.accessToken) {
						businessNumbersConfigureViewModel.current =
							new BusinessNumbersConfigureViewModel(
								user.accessToken
							);

						const response =
							await businessNumbersConfigureViewModel.current?.fetchListAsync(
								business.businessId
							);

						const aNumbers: IBusinessNumberModel[] = response
							.filter((number) => number.status === "Approved") // Filter only approved numbers
							.map((number) => {
								// Ensure managedNumberSettings is initialized
								if (!number.managedNumberSettings) {
									number.managedNumberSettings = {
										cPaaSProvider: "Vonage",
										screenSettings: defaultScreenSetting,
									};
								}
								return number; // Return the updated number
							});
						setApprovedBusinessNumbers(aNumbers); // Update the state
					}
				} catch (error) {
					console.error("Error fetching data:", error);
				}
			}
		};

		if (business) {
			fetchBusinessNumbers(); // Call when business is set
		}
	}, [business]); // Depend on `business` and `user`

	useEffect(() => {
		if (useLocationBasedCallRouting) {
			console.log("Location-based call routing is enabled.");
		} else {
			console.log("Location-based call routing is disabled.");
		}
	}, [useLocationBasedCallRouting]);

	const toggleAccordion = (index: number) => {
		setActiveIndex(activeIndex === index ? null : index);
	};

	const handleSubmitApplication = async () => {
		setApplicationSubmitDialogOpen(true);
		return;
	};

	const handleUnlink = (businessNumber: IBusinessNumberModel) => {
		setUnLinkedBusinessNumbers((prev) => [...prev, businessNumber]);
	};

	const handleLink = (businessNumber: IBusinessNumberModel) => {
		setUnLinkedBusinessNumbers((prev) =>
			prev.filter(
				(num) => num.businessNumber !== businessNumber.businessNumber
			)
		);
	};

	const toggleError = (value: boolean) => {
		setErrorDialogOpen((prevValue) => {
			if (prevValue !== value) {
				return value;
			}
			return prevValue;
		});
	};

	const closeError = () => {
		toggleError(false);
	};

	const handleCancel = () => {
		if (onCancelClick) {
			onCancelClick();
		}
	};

	const toggleShowDownloads = () => {
		setShowDownloads((prevValue) => !prevValue);
	};

	const toggleNumberDialog = () => {
		setNumberDialogOpen((prevValue) => !prevValue);
	};

	const toggleDeleteApplication = () => {
		setShowApplicationDeleteConfirmation((prevValue) => !prevValue);
	};

	const handleCheckboxChange = (eventName: string, isChecked: boolean) => {
		setFallbackEvents((prev) => {
			// If unchecked, remove the event
			if (!isChecked) {
				const updated = prev?.filter((event) => event !== eventName);
				return updated && updated.length > 0 ? updated : undefined; // Return undefined if empty
			}
			// If checked, add the event
			return prev ? [...prev, eventName] : [eventName];
		});
	};

	const handleOutgoingCallRoutingChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "PSTN") {
			setRouteOutgoingCallsTo("PSTN");
			if (
				application &&
				application.managedApplicationSettings &&
				application.managedApplicationSettings.outBoundCall
			) {
				application.managedApplicationSettings.outBoundCall.routingOption =
					"PSTN";
				application.managedApplicationSettings.outBoundCall.fallBackEvents =
					[];
			}
		} else if (selectedOption.value === "WEBRTC") {
			setRouteOutgoingCallsTo("WEBRTC");
			if (
				application &&
				application.managedApplicationSettings &&
				application.managedApplicationSettings.outBoundCall
			) {
				application.managedApplicationSettings.outBoundCall.routingOption =
					"WEBRTC";

				if (hasUnAnsweredEventSubscribed) {
					application.managedApplicationSettings.outBoundCall.fallBackEvents.push(
						"timeout"
					);
				}
				if (hasFailedEventSubscribed) {
					application.managedApplicationSettings.outBoundCall.fallBackEvents.push(
						"failed"
					);
				}
			}
		}
	};

	const handleIncomingCallRoutingChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "SIP") {
			setRouteIncomingCallsTo("SIP");
			if (
				application &&
				application.managedApplicationSettings &&
				application.managedApplicationSettings.inboundCall
			) {
				application.managedApplicationSettings.inboundCall.routingOption =
					"SIP";
			}
		} else if (selectedOption.value === "PSTN") {
			setRouteIncomingCallsTo("PSTN");
			if (
				application &&
				application.managedApplicationSettings &&
				application.managedApplicationSettings.inboundCall
			) {
				application.managedApplicationSettings.inboundCall.routingOption =
					"PSTN";
			}
		}
	};

	const convertToFallbackEvents = (
		strings: string[]
	): TOutboundCallFallBackEvent[] => {
		const validEvents: TOutboundCallFallBackEvent[] = ["timeout", "failed"]; // Define valid event strings
		return strings.filter((event): event is TOutboundCallFallBackEvent =>
			validEvents.includes(event as TOutboundCallFallBackEvent)
		);
	};

	if (loading) {
		return (
			<LoadingPlaceHolder message="We're setting things up for you. Just a moment!" />
		);
	}

	return (
		<>
			{/* Help Text */}
			{/* {showHelp === true && (
				<HelpText
					help={[
						"You can modify the branding configuration from this section. However, only certain details, such as branding for the numbers, can be updated here",
						"To make changes to other settings, such as business details or contact center configurations, please create a new application. If needed, you can delete this application by clicking the delete button at the bottom",
						"Please note, once deleted, the application cannot be recovered",
					]}
					alignment="center"
					onCloseClick={toggleHelp}
				/>
			)} */}

			{/* {showHelp === false && (
				<Box
					sx={{
						position: "relative",
						marginBottom: 3,
						marginTop: 3,
					}}
				>
					<IconButton
						onClick={toggleHelp}
						sx={{ position: "absolute", top: 0, right: 0 }}
					>
						<HelpIcon />
					</IconButton>
				</Box>
			)} */}

			{application && (
				<>
					{/* <span className="font-code">
						{JSON.stringify(application.managedApplicationSettings)}
					</span> */}

					<div className="font-Poppins flex flex-col items-end gap-6 w-full p-4">
						{/* Cards */}
						<div className="flex md:flex-col lg:flex-row items-start justify-start gap-2 w-full">
							<Card
								heading={"Business"}
								code={""}
								image={application.business?.logo?.signedUrl}
								name={application.business?.name}
							></Card>

							<Card
								heading={"Mobile Application"}
								code={application.brandingApplication?.code}
								name={application.brandingApplication?.name}
							></Card>

							{application.contactCentre && (
								<Card
									heading={"Contact center"}
									code={application.contactCentre.code}
									name={application.contactCentre.name}
								></Card>
							)}
						</div>
						{/* Cards */}

						{/* Name and Description */}
						<div className="w-full flex flex-col items-center justify-center gap-4 mt-4">
							<InputBox
								label={"Name"}
								type={"text"}
								required
								value={applicationFriendlyName}
								onChange={function (value: string): void {
									setHasChanges(true);
									setApplicationFriendlyName(value);
								}}
								maxLength={100}
							></InputBox>
							<textarea
								className="bg-slate-50 font-Poppins font-medium w-full border-b-2 border-gray-400"
								rows={4} // Number of visible rows
								maxLength={255}
								placeholder="Enter description here..."
								value={description} // Replace 'description' with your state variable
								onChange={(e) => {
									setHasChanges(true);
									setDescription(e.target.value);
								}} // Replace 'setDescription' with your setter function
							></textarea>
						</div>
						{/* Name and Description */}

						{/* Application Type */}
						<div className="flex items-start justify-start gap-2 w-full">
							<span className="font-semibold text-lg uppercase">
								Application Type
							</span>
						</div>
						{/* Application Type */}

						<div className="flex flex-col items-start justify-start gap-4 w-full">
							<RadioGroupSquare
								options={[
									{
										label: "Unmanaged Calls",
										value: "unmanaged",
										disabled: true,
									},
									{
										label: "Managed Calls",
										value: "managed",
										disabled: true,
									},
								]}
								defaultSelected={
									application.brandingOption ===
									"CPaaSManagedCallBranding"
										? "managed"
										: "unmanaged"
								}
								onChange={() => {
									console.log(``);
								}}
							/>
							<div className="w-full">
								{application.brandingOption ===
								"CPaaSManagedCallBranding" ? (
									<Alert
										text={
											"The Managed Calls option provides a complete calling solution by integrating SIP-based call routing with branded call experiences. This approach is ideal for businesses that require both branding and call handling capabilities."
										}
										type={"info"}
									></Alert>
								) : (
									<Alert
										text={
											"The Unmanaged Calls option is designed for businesses that only need branding support without relying on us to handle the calling process. In this model, businesses are responsible for call initiation and routing through their existing contact center setup."
										}
										type={"info"}
									></Alert>
								)}
							</div>
						</div>

						{/* Here */}
						{application.brandingOption ===
							"CPaaSManagedCallBranding" && (
							<div className="flex items-start justify-center gap-4 h-full w-full">
								<div className="flex w-full h-full">
									<BoxWithIcon
										icon={
											<OutgoingCallIcon className="w-4 h-4"></OutgoingCallIcon>
										}
										title={"Outbound Calling"}
									>
										<div className="flex flex-col items-start justify-center gap-6 w-full h-full p-2">
											<CheckBox
												title="Enabled"
												checked={true}
												onChange={function (
													value: boolean
												): void {
													console.log(`${value}`);
												}}
												disabled
											></CheckBox>
											{/* <InputBox
												label={"Call Announcement"}
												type={"text"}
												value={
													outboundCallAnnouncementText
												}
												onChange={function (
													value: string
												): void {
													if (
														application &&
														application.managedApplicationSettings
													) {
														application.managedApplicationSettings.outBoundCall.announcementText =
															value;
													}
													setOutboundCallAnnouncementText(
														value
													);
												}}
												maxLength={75}
											></InputBox> */}

											<span className="font-semibold text-lg text-sky-600">
												Route outgoing calls to
											</span>
											<RadioGroup
												options={[
													{
														label: `${application.brandingApplication?.name ?? "App"} `,
														value: "WEBRTC",
													},
													{
														label: "Mobile Network / PSTN",
														value: "PSTN",
													},
												]}
												defaultSelected={
													routeOutgoingCallsTo
												}
												onChange={
													handleOutgoingCallRoutingChange
												}
											/>
											{routeOutgoingCallsTo ===
												"WEBRTC" && (
												<div className="flex flex-col items-start justify-center gap-4 w-full">
													<InputBox
														label={
															"Ringer timeout in seconds"
														}
														type={"number"}
														value={`${ringerTimeout}`}
														maxLength={3} // Ensuring max length for 120
														onChange={(
															value: string
														): void => {
															const parsedValue =
																Number.parseInt(
																	value
																);

															if (
																isNaN(
																	parsedValue
																) ||
																parsedValue < 0
															) {
																setRingerTimeout(
																	10
																); // Default fallback value
															} else if (
																parsedValue >
																120
															) {
																setRingerTimeout(
																	120
																); // Max limit
															} else {
																setRingerTimeout(
																	parsedValue
																);
															}
														}}
													></InputBox>

													<span className="font-semibold text-lg text-sky-600">
														Fallback to PSTN call
														when the call is
													</span>
													<div className="flex items-start justify-center">
														<div className="flex items-center justify-center py-2 space-x-2">
															<CheckBox
																title="Unanswered"
																checked={
																	hasUnAnsweredEventSubscribed
																}
																onChange={(
																	value
																) => {
																	setHasUnAnsweredEventSubscribed(
																		value
																	);
																	handleCheckboxChange(
																		"timeout",
																		value
																	);

																	if (
																		application &&
																		application.managedApplicationSettings &&
																		application
																			.managedApplicationSettings
																			.outBoundCall
																	) {
																		const fallBackEvents =
																			application
																				.managedApplicationSettings
																				.outBoundCall
																				.fallBackEvents;

																		if (
																			!value
																		) {
																			// Remove the "timeout" event if unchecked
																			application.managedApplicationSettings.outBoundCall.fallBackEvents =
																				fallBackEvents.filter(
																					(
																						existingEvent
																					) =>
																						existingEvent !==
																						"timeout"
																				);
																		} else {
																			// Add the "timeout" event if checked, ensuring no duplicates
																			if (
																				!fallBackEvents.includes(
																					"timeout"
																				)
																			) {
																				application.managedApplicationSettings.outBoundCall.fallBackEvents.push(
																					"timeout"
																				);
																			}
																		}
																	}
																}}
															></CheckBox>
															{/* {hasUnAnsweredEventSubscribed && (
																<>
																	<div className="w-6">
																		<InputBox
																			label={
																				""
																			}
																			type={
																				"text"
																			}
																			value={`${webRTCCallTimeoutInSeconds}`}
																			onChange={function (
																				value: string
																			): void {
																				// Parse the value as an integer
																				const parsedValue =
																					Number.parseInt(
																						value
																					);

																				// Check if the parsed value is a valid number
																				if (
																					!Number.isNaN(
																						parsedValue
																					)
																				) {
																					setWebRTCCallTimeoutInSeconds(
																						parsedValue
																					);
																				}
																			}}
																			noCross
																			maxLength={
																				2
																			}
																		></InputBox>
																	</div>
																	<span className="font-Poppins font-semibold text-md">
																		seconds
																	</span>
																</>
															)} */}
															<CheckBox
																title="Failed"
																checked={
																	hasFailedEventSubscribed
																}
																onChange={(
																	value
																) => {
																	setHasFailedEventSubscribed(
																		value
																	);
																	handleCheckboxChange(
																		"failed",
																		value
																	);

																	if (
																		application &&
																		application.managedApplicationSettings &&
																		application
																			.managedApplicationSettings
																			.outBoundCall
																	) {
																		const fallBackEvents =
																			application
																				.managedApplicationSettings
																				.outBoundCall
																				.fallBackEvents;

																		if (
																			!value
																		) {
																			// Remove the "timeout" event if unchecked
																			application.managedApplicationSettings.outBoundCall.fallBackEvents =
																				fallBackEvents.filter(
																					(
																						existingEvent
																					) =>
																						existingEvent !==
																						"failed"
																				);
																		} else {
																			// Add the "timeout" event if checked, ensuring no duplicates
																			if (
																				!fallBackEvents.includes(
																					"failed"
																				)
																			) {
																				application.managedApplicationSettings.outBoundCall.fallBackEvents.push(
																					"failed"
																				);
																			}
																		}
																	}
																}}
															></CheckBox>
														</div>
													</div>
													{/* <div className="w-full">
													<Alert
														text={
															"Outgoing calls are charged @ A$0.016/min"
														}
														type={"info"}
													></Alert>
												</div> */}
												</div>
											)}
										</div>
									</BoxWithIcon>
								</div>
								<div className="flex w-full h-full">
									<BoxWithIcon
										icon={
											<IncomingCallIcon className="w-4 h-4"></IncomingCallIcon>
										}
										title={"Inbound Calling"}
									>
										<div className="flex flex-col items-start justify-center gap-6 w-full h-full p-2">
											<CheckBox
												title="Enabled"
												checked={enableInboundCalling}
												onChange={function (
													value: boolean
												): void {
													if (
														application &&
														application.managedApplicationSettings
													) {
														if (!value) {
															application.managedApplicationSettings.inboundCall =
																undefined;
														} else {
															application.managedApplicationSettings.inboundCall =
																{
																	routingOption:
																		routeIncomingCallsTo,
																	announcementText:
																		inboundCallAnnouncementText,
																	sipDomain:
																		inboundSipDomain,
																	phoneNumber:
																		phoneNumber,
																};
														}
													}
													setEnableInboundCalling(
														value
													);
												}}
											></CheckBox>

											{enableInboundCalling && (
												<>
													<InputBox
														label={
															"Call Announcement"
														}
														type={"text"}
														value={
															inboundCallAnnouncementText
														}
														onChange={function (
															value: string
														): void {
															if (
																application &&
																application.managedApplicationSettings &&
																application
																	.managedApplicationSettings
																	.inboundCall
															) {
																application.managedApplicationSettings.inboundCall.announcementText =
																	value;
															}
															setInboundCallAnnouncementText(
																value
															);
														}}
														maxLength={75}
													></InputBox>
													{/* Temp remove this  */}
													<div className="hidden">
														<div className="flex items-center justify-center gap-4">
															<CheckBox
																title="Enable location based call routing"
																checked={
																	useLocationBasedCallRouting
																}
																onChange={function (
																	value: boolean
																): void {
																	setLocationBasedCallRouting(
																		value
																	);
																}}
															></CheckBox>
														</div>
													</div>
													{useLocationBasedCallRouting && (
														<>
															<span>
																<HyperLink
																	href={""}
																	label={
																		"Download data template"
																	}
																></HyperLink>
															</span>
															<UploadButton
																buttonType="File"
																onUploadSuccess={function (
																	file: IFileInfo
																): void {
																	console.log(
																		file.signedUrl
																	);
																}}
															></UploadButton>
														</>
													)}
													<span className="font-semibold text-lg text-sky-600">
														Route incoming calls to
													</span>
													<RadioGroup
														options={[
															{
																label: `SIP`,
																value: "SIP",
															},
															{
																label: "Phone number",
																value: "PSTN",
															},
														]}
														defaultSelected={
															routeIncomingCallsTo
														}
														onChange={
															handleIncomingCallRoutingChange
														}
													/>
													{routeIncomingCallsTo ===
													"SIP" ? (
														<InputBox
															label={
																"Inbound SIP Domain with port"
															}
															type={"text"}
															required={
																routeIncomingCallsTo ===
																"SIP"
															}
															value={
																inboundSipDomain
															}
															onChange={function (
																value: string
															): void {
																if (
																	application &&
																	application.managedApplicationSettings &&
																	application
																		.managedApplicationSettings
																		.inboundCall
																) {
																	application.managedApplicationSettings.inboundCall.sipDomain =
																		value;
																}
																setInboundSipDomain(
																	value
																);
															}}
															helpText="e.g. (securedcalls.3cx.com.au:5060)"
														></InputBox>
													) : (
														<>
															<PhoneNumberField
																phoneNumber={
																	inboundPhoneNumber
																}
																value={
																	phoneNumber
																}
																onChange={(
																	phoneNumber,
																	value
																) => {
																	if (
																		application &&
																		application.managedApplicationSettings &&
																		application
																			.managedApplicationSettings
																			.inboundCall
																	) {
																		application.managedApplicationSettings.inboundCall.phoneNumber =
																			value;
																	}

																	setPhoneNumber(
																		value
																	);
																}}
																showCountry={
																	false
																}
																required={
																	routeIncomingCallsTo ===
																	"PSTN"
																}
															></PhoneNumberField>
														</>
													)}

													{/* <div className="w-full">
													<Alert
														text={
															"Inbound calls are charged @ A$0.016/min"
														}
														type={"info"}
													></Alert>
												</div> */}
												</>
											)}
										</div>
									</BoxWithIcon>
								</div>
							</div>
						)}
						{/* Here */}

						{/* Branding options */}
						<div className=" flex flex-col items-start justify-center w-full gap-4">
							{/* <>
							{unLinkedBusinessNumbers &&
								unLinkedBusinessNumbers.map((number, index) => {
									<div
										className="bg-red-100 flex flex-col items-center justify-center gap-4"
										key={index}
									>
										<span className="font-semibold text-lg text-black">
											{number.businessNumber}
										</span>
									</div>;
								})}
						</> */}

							<span className="font-semibold text-lg uppercase">
								Branding Numbers (
								{approvedBusinessNumbers.length})
							</span>
							{application && application.business && (
								<>
									<div className="flex items-center justify-start w-full">
										<ButtonAddNew
											text="Add more"
											onClick={toggleNumberDialog}
											disabled={
												approvedBusinessNumbers.length ===
												0
											}
										></ButtonAddNew>
									</div>
									<>
										<div className="flex items-center justify-start w-full pt-3">
											<InputBox
												value={
													businessNumberFilteredValue
												}
												label={"Filter"}
												type={"text"}
												onChange={function (
													value: string
												): void {
													setBusinessNumberFilteredValue(
														value
													);
												}}
											></InputBox>
										</div>
										{/* todo: consider to move this to its own component later. */}
										{applicationNumbers && (
											<>
												<div
													id="accordion"
													className="w-full"
												>
													<div className="flex items-center justify-start w-full p-2">
														<span className="font-semibold text-sm">
															Click on individual
															numbers to configure
															branding.
														</span>
													</div>
													{applicationNumbers
														.filter(
															(p) =>
																((!businessNumberFilteredValue ||
																	p.businessNumber
																		?.toLowerCase()
																		.includes(
																			businessNumberFilteredValue
																		)) &&
																	p.status ===
																		"Active") ||
																p.status ===
																	"Approved"
														)
														.map((item, index) => (
															<div
																key={index}
																className="pb-2"
															>
																<h2
																	className={`${item.hasUnsavedChanges ? "bg-gradient-to-r from-sky-400 to-sky-200 hover:bg-sky-200" : ""} flex items-center justify-between w-full py-3 px-4 pe-6 font-medium text-gray-800 bg-slate-200 rounded border-gray-100 hover:bg-gray-200`}
																>
																	{/* Accordion toggle area */}
																	<button
																		type="button"
																		className="flex items-center justify-start gap-2 w-full"
																		onClick={() =>
																			toggleAccordion(
																				index
																			)
																		}
																		aria-expanded={
																			activeIndex ===
																			index
																		}
																		aria-controls={`accordion-body-${index}`}
																	>
																		<img
																			key={
																				item.businessNumber
																			}
																			className="w-12 h-12 rounded-full flex-shrink-0 drop-shadow-md"
																			src={
																				item
																					.branding
																					?.imageFile
																					?.signedUrl ||
																				business
																					?.logo
																					?.signedUrl
																			}
																			alt=""
																		/>
																		<span
																			className={`${
																				item.hasUnsavedChanges
																					? "text-white"
																					: "text-gray-900"
																			} ${unLinkedBusinessNumbers.some((num) => num.businessNumber === item.businessNumber) ? "line-through" : ""} font-semibold text-lg`}
																		>
																			<PhoneNumberDisplay
																				number={
																					item.businessNumber
																				}
																				hasPlus={
																					false
																				}
																			></PhoneNumberDisplay>
																		</span>
																		<button
																			className="pr-2 cursor-copy"
																			onClick={(
																				e
																			) => {
																				e.stopPropagation();
																				ClipboardService.copyToClipboard(
																					"+" +
																						item.businessNumber
																				);
																			}}
																		>
																			<CopyIcon className="w-4 h-4 text-gray-900"></CopyIcon>
																		</button>
																	</button>

																	{/* Action buttons */}
																	<div className="flex items-center justify-end gap-2 w-fit">
																		{/* Remove button */}
																		<StatusBadge
																			status={
																				item.status
																			}
																		></StatusBadge>
																		{item.hasUnsavedChanges && (
																			<div className="rounded-2xl bg-orange-500 w-full">
																				<span
																					className={`py-1 px-4 rounded-full text-sm font-semibold text-white text-nowrap`}
																				>
																					{
																						"Modified"
																					}
																				</span>
																			</div>
																		)}

																		{item.applicationBundleCode ? (
																			<div className="rounded-2xl w-full flex items-center justify-center gap-2">
																				<button
																					className={`py-1 px-4 rounded-md text-sm font-semibold text-white text-nowrap ${
																						unLinkedBusinessNumbers.some(
																							(
																								num
																							) =>
																								num.businessNumber ===
																								item.businessNumber
																						)
																							? "bg-sky-500"
																							: "bg-red-500"
																					}`}
																					onClick={() => {
																						if (
																							unLinkedBusinessNumbers.some(
																								(
																									num
																								) =>
																									num.businessNumber ===
																									item.businessNumber
																							)
																						) {
																							handleLink(
																								item
																							); // Call the link function
																						} else {
																							handleUnlink(
																								item
																							); // Call the unlink function
																						}
																					}}
																				>
																					{unLinkedBusinessNumbers.some(
																						(
																							num
																						) =>
																							num.businessNumber ===
																							item.businessNumber
																					)
																						? "Link"
																						: "Unlink?"}
																				</button>
																			</div>
																		) : (
																			<div className="rounded-2xl bg-blue-500 w-full">
																				<span
																					className={`py-1 px-4 rounded-full text-sm font-semibold text-white text-nowrap`}
																				>
																					{
																						"Pending"
																					}
																				</span>
																			</div>
																		)}
																	</div>
																</h2>

																{/* Accordion content */}
																<div
																	id={`accordion-body-${index}`}
																	className={`overflow-auto transition-all duration-300 ${
																		activeIndex ===
																		index
																			? "h-full"
																			: "max-h-0"
																	}`}
																>
																	<div className="h-full pt-2 text-gray-500">
																		{/* {item.content} */}
																		<>
																			{business && (
																				<BrandingConfiguration
																					business={
																						business
																					}
																					number={
																						item
																					}
																					scProvidedNumber={
																						true
																					}
																					onChange={(
																						value
																					) => {
																						setApplicationNumbers(
																							(
																								prevNumbers
																							) => {
																								// Create a deep copy to prevent shallow replacements
																								const updatedNumbers =
																									[
																										...prevNumbers,
																									]; // Clone existing state

																								// Find the index of the changed number
																								const index =
																									updatedNumbers.findIndex(
																										(
																											num
																										) =>
																											num.businessNumber ===
																											value.businessNumber
																									);

																								if (
																									index !==
																									-1
																								) {
																									// Update only the required element in the copy
																									updatedNumbers[
																										index
																									] =
																										{
																											...updatedNumbers[
																												index
																											],
																											...value,
																										};
																								}

																								// Return the updated copy
																								return updatedNumbers;
																							}
																						);
																					}}
																					showInAppModel={
																						application.brandingOption ===
																						"CPaaSManagedCallBranding"
																					}
																					showInAppBranding={
																						routeOutgoingCallsTo ===
																						"WEBRTC"
																					}
																					inboundCallSettings={
																						enableInboundCalling
																							? {
																									announcementText:
																										inboundCallAnnouncementText,
																									sipEndpointUrl:
																										inboundSipDomain,
																								}
																							: undefined
																					}
																					appName={
																						application
																							.brandingApplication
																							.name
																					}
																				/>
																			)}
																		</>
																	</div>
																</div>
															</div>
														))}
												</div>
											</>
										)}
									</>
								</>
							)}
						</div>

						<ActionPanel
							primaryButtonLabel={"Submit"}
							primaryButtonEnabled={
								(application.business != undefined &&
									mobileApp != undefined) ||
								(enableInboundCalling &&
									inboundSipDomain !== "")
							}
							secondaryButtonLabel={"Cancel"}
							secondaryButtonEnabled={true}
							primaryButtonAction={handleSubmitApplication}
							secondaryButtonAction={handleCancel}
						></ActionPanel>

						{/* Dialog for Downloads */}
						<Dialog
							open={showDownloads}
							onClose={toggleShowDownloads}
							maxWidth="lg"
							fullWidth
							PaperProps={{
								style: {
									backgroundColor: "transparent",
									boxShadow: "none",
									width: "100%",
									alignContent: "centre",
									justifyContent: "center",
								},
							}}
						>
							<DialogContent
								style={{
									padding: 0,
									margin: 0,
									width: "100%", // Ensure it takes up the full width
									display: "flex",
									justifyContent: "center",
								}}
							>
								{application && (
									<ApplicationDownloads
										application={application}
										onClose={toggleShowDownloads}
									/>
								)}
							</DialogContent>
						</Dialog>

						{/* Application submission dialog */}
						<Dialog
							open={applicationSubmitDialogOpen}
							onClose={(event, reason) => {
								// Prevent closing when clicking outside or pressing Escape
								if (
									reason !== "backdropClick" &&
									reason !== "escapeKeyDown"
								) {
									toggleNumberDialog(); // Handle other close actions, if required
								}
							}}
							maxWidth="md"
							fullWidth
							PaperProps={{
								style: {
									backgroundColor: "transparent",
									boxShadow: "none",
									width: "100%",
									alignContent: "centre",
									justifyContent: "center",
								},
							}}
						>
							<DialogContent
								style={{
									padding: 0,
									margin: 0,
									width: "100%", // Ensure it takes up the full width
									display: "flex",
									justifyContent: "center",
								}}
							>
								<Banner
									label={`Updating application - ${applicationFriendlyName}`}
								>
									<ApplicationSubmitProcessor
										applicationId={
											application.applicationId
										}
										applicationName={
											applicationFriendlyName
										}
										description={description}
										onClose={() => {
											setApplicationSubmitDialogOpen(
												false
											);
											handleCancel();
										}}
										numbersToProcess={applicationNumbers}
										numbersToUnlink={
											unLinkedBusinessNumbers
										}
										businessId={
											application.business.businessId
										}
										brandingOption={
											application.brandingOption
										}
										managedApplicationSettings={
											application.managedApplicationSettings
										}
									></ApplicationSubmitProcessor>
								</Banner>
							</DialogContent>
						</Dialog>

						{/* Dialog for business numbers */}
						<Dialog
							open={numberDialogOpen}
							onClose={toggleNumberDialog}
							maxWidth="md"
							fullWidth
							PaperProps={{
								style: {
									backgroundColor: "transparent",
									boxShadow: "none",
									width: "100%",
									alignContent: "centre",
									justifyContent: "center",
								},
							}}
						>
							<DialogContent
								style={{
									padding: 0,
									margin: 0,
									width: "100%", // Ensure it takes up the full width
									display: "flex",
									justifyContent: "center",
								}}
							>
								<BusinessNumbersList
									businessNumbers={approvedBusinessNumbers}
									onAddNewClick={function (): void {
										console.log(`not required.`);
									}}
									onClose={toggleNumberDialog}
									onSelect={function (
										selectedNumbers: IBusinessNumberModel[]
									): void {
										setApplicationNumbers((prevNumbers) => {
											// Create a map of the existing business numbers for quick lookup
											const existingNumbersMap = new Map(
												prevNumbers.map((num) => [
													num.businessNumber,
													num,
												])
											);

											// Add or update numbers from selectedNumbers
											selectedNumbers.forEach((num) => {
												existingNumbersMap.set(
													num.businessNumber,
													{
														...existingNumbersMap.get(
															num.businessNumber
														),
														...num,
														selected: true, // Mark as selected
													}
												);
											});

											// Remove numbers not in selectedNumbers from businessNumbers unless they have `applicationBundleCode`
											const selectedNumbersSet = new Set(
												selectedNumbers.map(
													(num) => num.businessNumber
												)
											);
											const updatedNumbers = Array.from(
												existingNumbersMap.values()
											).filter(
												(num) =>
													selectedNumbersSet.has(
														num.businessNumber
													) ||
													num.applicationBundleCode // Keep numbers with `applicationBundleCode`
											);

											// Update approvedBusinessNumbers to mark unselected numbers
											setApprovedBusinessNumbers(
												(prevApprovedNumbers) => {
													const approvedNumbersMap =
														new Map(
															prevApprovedNumbers.map(
																(num) => [
																	num.businessNumber,
																	num,
																]
															)
														);

													// Update the selected property for numbers in approvedBusinessNumbers
													updatedNumbers.forEach(
														(num) => {
															if (
																num.status ===
																"Approved"
															) {
																approvedNumbersMap.set(
																	num.businessNumber,
																	{
																		...approvedNumbersMap.get(
																			num.businessNumber
																		),
																		...num,
																		selected:
																			num.selected,
																	}
																);
															}
														}
													);

													// Mark numbers not in selectedNumbers as unselected
													prevApprovedNumbers.forEach(
														(num) => {
															if (
																!selectedNumbersSet.has(
																	num.businessNumber
																) &&
																!num.applicationBundleCode // Only mark as unselected if it doesn't have `applicationBundleCode`
															) {
																approvedNumbersMap.set(
																	num.businessNumber,
																	{
																		...num,
																		selected:
																			false,
																	}
																);
															}
														}
													);

													return Array.from(
														approvedNumbersMap.values()
													);
												}
											);

											return updatedNumbers;
										});
									}}
									selectMode={true}
								></BusinessNumbersList>
							</DialogContent>
						</Dialog>

						{/* <Dialog
						open={brandingDialogOpen}
						onClose={toggleBrandingDialog}
						maxWidth="lg"
						fullWidth
						PaperProps={{
							style: {
								backgroundColor: "transparent",
								boxShadow: "none",
								width: "100%",
								alignContent: "centre",
								justifyContent: "center",
							},
						}}
					>
						<DialogContent>
							<ConfigureBranding
								businessId={
									application?.business?.businessCode ?? ""
								}
								businessName={
									application?.business?.name ?? "placeholder"
								}
								defaultIntent="demo intent"
								number="1324354678"
								image={
									application?.business?.logo?.signedUrl ?? ""
								}
							></ConfigureBranding>
						</DialogContent>
					</Dialog> */}

						{/* showApplicationCredentials */}
						<Dialog
							open={showApplicationCredentials}
							onClose={() => setShowApplicationCredentials(false)}
							maxWidth="lg"
							fullWidth
						>
							<DialogContent
								style={{
									padding: 0,
									margin: 0,
									width: "100%", // Ensure it takes up the full width
									display: "flex",
									justifyContent: "center",
								}}
							>
								<ApplicationCredentials
									brandingAppCode={application.applicationId}
									ccId={application.contactCentre?.code ?? ""}
									isNewApp={true}
									onOkClick={handleCancel}
								></ApplicationCredentials>
							</DialogContent>
						</Dialog>

						{/* showApplicationDeleteConfirmation */}
						<Dialog
							open={showApplicationDeleteConfirmation}
							onClose={() => {
								toggleDeleteApplication();
								setDeleteConfirmationText("");
								setApplicationDeleteError("");
							}}
							maxWidth="md"
							fullWidth
							PaperProps={{
								style: {
									backgroundColor: "transparent",
									boxShadow: "none",
									width: "100%",
									alignContent: "centre",
									justifyContent: "center",
								},
							}}
						>
							<DialogContent
								style={{
									padding: 0,
									margin: 0,
									width: "100%", // Ensure it takes up the full width
									display: "flex",
									justifyContent: "center",
								}}
							>
								<Banner
									backgroundColor="bg-red-600"
									label="Delete Branding Application"
									buttonBorderColor="border-white"
								>
									<div className="bg-red-600 flex flex-col items-center justify-center gap-6">
										<span className="font-Poppins font-semibold text-xl text-white">
											Are you sure you want to delete the
											branding application?
										</span>
										<div className="bg-slate-50 flex flex-col items-center justify-center gap-4 w-full pt-4 p-4">
											<InputBox
												label={`Enter the ${DELETEBRANDINGAPPLICATION} to confirm`}
												type={"text"}
												value={deleteConfirmationText}
												onChange={function (
													value: string
												): void {
													setDeleteConfirmationText(
														value
													);
												}}
											></InputBox>

											<ActionPanel
												primaryButtonLabel={"Yes"}
												primaryButtonEnabled={
													deleteConfirmationText ===
													DELETEBRANDINGAPPLICATION
												}
												secondaryButtonLabel={"No"}
												secondaryButtonEnabled={true}
												primaryButtonAction={async function (): Promise<void> {
													try {
														const result =
															await applicationListViewModel.current?.deleteBrandingApplicationAsync(
																application
															);

														setDeleteConfirmationText(
															""
														);
														toggleDeleteApplication();
														setApplicationDeleteError(
															""
														);
														if (result) {
														}
													} catch (error) {
														setApplicationDeleteError(
															"Failed to delete application"
														);
														console.error(
															"Failed to delete application:",
															error
														);
													}
												}}
												secondaryButtonAction={() => {
													setDeleteConfirmationText(
														""
													);
													toggleDeleteApplication();
													setApplicationDeleteError(
														""
													);
												}}
											/>
										</div>
									</div>
								</Banner>
							</DialogContent>
						</Dialog>

						<ErrorPopup
							message={error}
							open={errorDialogOpen}
							onClose={closeError}
						></ErrorPopup>
					</div>
				</>
			)}
		</>
	);
};

export default ApplicationEdit;
